// @flow
import { RESIDENCE_TYPES } from 'pages/AffordaIdentifyingDetails/constants';
import {
  booleanToYesNo,
  getBooleanFromYesNo,
  getValueOrEmptyString,
  getValueOrNull,
  getValueOrUndefined,
} from 'utils';
import { formatPhoneNumber } from '@fortress-technology-solutions/fortress-component-library/utils';

export interface Employment {
  annualSalary: string;
  employerName: string;
  from: string;
  phoneNumber: string;
  position: string;
  supervisorName: string;
  to: string;
  address: {
    city: string,
  };
}

export const formatDoNotCurrentlyOwnRentExplanation = ({
  canContactToVerifySituation,
  contactName,
  explainTheSituation,
  nameOfPersonYouLiveWith,
  ownOrRentOrNotCurrently,
  phoneNumber,
  relationshipToYou,
}) => {
  let value = explainTheSituation;

  if (ownOrRentOrNotCurrently === RESIDENCE_TYPES.LIVE_WITH_FAMILY_OR_FRIENDS) {
    value = `Lived with family or friends:
    Name of person(s) I lived with: ${nameOfPersonYouLiveWith} (${relationshipToYou}). Phone number: ${formatPhoneNumber(
      phoneNumber,
    )}`;
  } else if (
    ownOrRentOrNotCurrently === RESIDENCE_TYPES.OTHER &&
    canContactToVerifySituation === 'yes'
  ) {
    value = `Can contact to verify: Contact name: ${contactName} (${relationshipToYou}). Phone number: ${formatPhoneNumber(
      phoneNumber,
    )}. ${explainTheSituation}`;
  }

  return value;
};

const getValueWithLengthOrUndefined = (value1, value2) => {
  return getValueOrUndefined(value1) ?? getValueOrUndefined(value2);
};

// There is an assignAddress function (and other utils functions) in the backend inside
// fortress-backend/src/domain/helpers/applications/index.js
// but it's not in sync with this one for now
export const assignAddress = (
  initialResidence = {},
  updateResidence = {},
  states,
) => {
  const isRent = updateResidence.residence === RESIDENCE_TYPES.RENT;
  const updatedState = getValueWithLengthOrUndefined(
    updateResidence?.address?.state ?? updateResidence?.state,
    initialResidence.state,
  );
  const state =
    states.find(({ code, id }) => code === updatedState || id === updatedState)
      ?.id ?? null;

  let ownOrRentOrNotCurrently = getValueWithLengthOrUndefined(
    updateResidence?.ownOrRent?.ownOrRentOrNotCurrently ??
      updateResidence?.residence,
    initialResidence?.type,
  );

  const canContactToVerifySituation = getValueWithLengthOrUndefined(
    updateResidence.canContactToVerifySituation,
    initialResidence.canContactToVerifySituation,
  );

  const explainTheSituation = getValueWithLengthOrUndefined(
    updateResidence.explainTheSituation,
    initialResidence.explainTheSituation,
  );

  const phoneNumber = getValueWithLengthOrUndefined(
    updateResidence.phoneNumber,
    initialResidence.phoneNumber,
  );

  const doNotCurrentlyOwnRentExplanation =
    formatDoNotCurrentlyOwnRentExplanation({
      canContactToVerifySituation,
      contactName: updateResidence.contactName,
      explainTheSituation,
      nameOfPersonYouLiveWith: updateResidence.nameOfPersonYouLiveWith,
      ownOrRentOrNotCurrently,
      phoneNumber,
      relationshipToYou: updateResidence.relationshipToYou,
    });

  const lenderPhoneNumber = getValueWithLengthOrUndefined(
    updateResidence?.ownOrRent?.lenderPhoneNumber ??
      updateResidence?.phoneNumber,
    initialResidence.lenderPhoneNumber,
  );

  // Map LIVE_WITH_FAMILY_OR_FRIENDS => OTHER
  if (ownOrRentOrNotCurrently === RESIDENCE_TYPES.LIVE_WITH_FAMILY_OR_FRIENDS) {
    ownOrRentOrNotCurrently = RESIDENCE_TYPES.OTHER;
  }

  const addressPayload = {
    monthlyPayment: getValueWithLengthOrUndefined(
      updateResidence.monthlyPayment,
      initialResidence.monthlyPay,
    ),
    dateMovedIn: getValueWithLengthOrUndefined(
      updateResidence.dateMovedIn ?? updateResidence.moveInDate,
      initialResidence.moveInDate,
    ),
    ownOrRent: {
      ownOrRentOrNotCurrently: getValueOrUndefined(ownOrRentOrNotCurrently),
      lenderName: getValueWithLengthOrUndefined(
        isRent
          ? updateResidence?.ownOrRent?.landLordOrCondoOrApartmentName ??
              updateResidence?.landlordCondoApartmentName
          : updateResidence?.ownOrRent?.lenderName ?? updateResidence?.lender,
        initialResidence.lenderName,
      ),
      lenderPhoneNumber,
      // lenderName is whats used for this value in fortress
      landLordOrCondoOrApartmentName: getValueWithLengthOrUndefined(
        updateResidence?.ownOrRent?.lenderName ?? updateResidence?.lender,
        initialResidence.lenderName,
      ),
      landLordOrCondoOrApartmentNamePhoneNumber: lenderPhoneNumber,
      doNotCurrentlyOwnRentExplanation: getValueWithLengthOrUndefined(
        updateResidence?.ownOrRent?.doNotCurrentlyOwnRentExplanation,
        initialResidence?.reasonOther ??
          doNotCurrentlyOwnRentExplanation ??
          initialResidence?.ownOrRent?.doNotCurrentlyOwnRentExplanation,
      ),
      reasonForMoving: getValueWithLengthOrUndefined(
        updateResidence?.ownOrRent?.reasonForMoving ??
          updateResidence?.reasonForMoving,
        initialResidence?.ownOrRent?.reasonForMoving ??
          initialResidence?.reasonForMoving,
      ),
    },
    address: {
      street: getValueWithLengthOrUndefined(
        updateResidence?.address?.street ?? updateResidence?.streetAddress,
        initialResidence.street,
      ),
      city: getValueWithLengthOrUndefined(
        updateResidence?.address?.city ?? updateResidence?.city,
        initialResidence.city,
      ),
      zipCode: getValueWithLengthOrUndefined(
        updateResidence?.address?.zipCode ?? updateResidence?.zipCode,
        initialResidence.zipCode,
      ),
      state: getValueOrUndefined(state),
    },
  };

  return addressPayload;
};

export const assignContactInformation = (
  initialContactInformation = {},
  updateContactInformation = {},
) => {
  return {
    phoneNumber:
      getValueOrNull(updateContactInformation?.phoneNumber) ??
      getValueOrNull(initialContactInformation?.phoneNumber) ??
      '9999999999',
    emailAddress:
      getValueOrNull(updateContactInformation?.emailAddress) ??
      getValueOrNull(initialContactInformation?.emailAddress) ??
      '',
  };
};

export const assignHandicappedField = (
  updateHandicapped = null,
  initialHandicapped = null,
) => {
  const handicappedValue = updateHandicapped ?? initialHandicapped;
  return handicappedValue && typeof handicappedValue === 'string'
    ? handicappedValue
    : booleanToYesNo(handicappedValue);
};

const assignEmployment = (initialJob = {}, updateJob = {}): Employment => {
  return {
    annualSalary: getValueWithLengthOrUndefined(
      updateJob.annualSalary,
      initialJob.annualSalary,
    ),
    employerName: getValueWithLengthOrUndefined(
      updateJob.employerName,
      initialJob.employerName,
    ),
    from: getValueWithLengthOrUndefined(updateJob.from, initialJob.from),
    phoneNumber: getValueWithLengthOrUndefined(
      updateJob.phoneNumber,
      initialJob.employerPhone,
    ),
    position: getValueWithLengthOrUndefined(
      updateJob.position,
      initialJob.position,
    ),
    supervisorName: getValueWithLengthOrUndefined(
      updateJob.supervisorName,
      initialJob.supervisorName,
    ),
    to: getValueWithLengthOrUndefined(updateJob.to, initialJob.to),
    address: {
      city: getValueWithLengthOrUndefined(
        updateJob?.address?.city,
        initialJob.city,
      ),
      street: getValueWithLengthOrUndefined(
        updateJob?.address?.street,
        initialJob.employerStreetAddress,
      ),
      zipCode: getValueWithLengthOrUndefined(
        updateJob?.address?.zipCode,
        initialJob.zipCode,
      ),
      state: getValueWithLengthOrUndefined(
        updateJob?.address?.state,
        initialJob.state,
      ),
    },
  };
};

/**
 * Updates minors information by merging initial and update data, and removing unwanted properties
 * @param {Array} initialMinorsInformation - Initial minors information array
 * @param {Array} updateMinorsInformation - Update minors information array
 * @returns {Array} - Updated minors information array with unwanted properties removed
 */
export const getMinorsInformationUpdate = (
  initialMinorsInformation = [],
  updateMinorsInformation = [],
) => {
  // If there's no initial data but we have updates, return all updates
  if (
    initialMinorsInformation.length === 0 &&
    updateMinorsInformation.length > 0
  ) {
    return updateMinorsInformation;
  }

  // Create a map of existing minors using a composite key of firstName + lastName + dateOfBirth
  const minorsMap = new Map();

  initialMinorsInformation.forEach((minor) => {
    minorsMap.set(minor.id, minor);
  });

  // Update or add new minors
  updateMinorsInformation.forEach((minor) => {
    minorsMap.set(minor.id, {
      ...minorsMap.get(minor.id),
      ...minor,
    });
  });

  // Convert back to array and remove unwanted properties
  return Array.from(minorsMap.values()).map((minor) => {
    const disability = { ...minor.disability };

    if (minor?.disability?.hasDisability === 'notWishToProvide') {
      disability.disabilityHearing = null;
      disability.disabilityMobile = null;
      disability.disabilityOther = null;
      disability.disabilityVisual = null;
    }

    if (typeof minor.handicapped === 'boolean') {
      minor.handicapped = booleanToYesNo(minor.handicapped);
    }

    const cleanedMinor = { ...minor, disability };
    [
      'applicant',
      'MINCNumber',
      'customerDisability',
      'customerDisabilityId',
      'suffix',
    ].forEach((key) => delete cleanedMinor[key]);

    return cleanedMinor;
  });
};

type AnticipateChangeEmployer = 'yes' | 'no' | null;

const getAnticipateChangeEmployer = (
  initialAnticipateChangeEmployer: AnticipateChangeEmployer,
  updateAnticipateChangeEmployer: AnticipateChangeEmployer,
) => {
  const updatedValue =
    updateAnticipateChangeEmployer ?? initialAnticipateChangeEmployer;

  if (typeof updatedValue === 'string') {
    return updatedValue;
  }

  return booleanToYesNo(updatedValue);
};

export const assignOtherInformation = ({ initialData, update }) => {
  return {
    ...initialData?.otherInformation,
    aboutProperty: getValueOrNull(
      update?.otherInformation?.aboutProperty ??
        initialData?.otherInformation?.aboutProperty,
    ),
    referralTypeId: getValueOrNull(
      update?.otherInformation?.referralTypeId,
      initialData?.otherInformation?.referralTypeId,
    ),
    pets: getValueOrNull(
      update?.otherInformation?.pets ??
        booleanToYesNo(initialData?.otherInformation?.pets),
    ),
    convicted: getValueOrNull(
      update?.otherInformation?.convicted ??
        booleanToYesNo(initialData?.otherInformation?.convicted),
    ),
    convictedDescription: getValueOrNull(
      update?.otherInformation?.convictedDescription ??
        initialData?.otherInformation?.convictedDescription,
    ),
    bankruptcy: getValueOrNull(
      update?.otherInformation?.bankruptcy ??
        booleanToYesNo(initialData?.otherInformation?.bankruptcy),
    ),
    bankruptcyCaseFiled: getValueOrNull(
      update?.otherInformation?.bankruptcyCaseFiled ??
        initialData?.otherInformation?.bankruptcyCaseFiled,
    ),
    bankruptcyDateFiled: getValueOrNull(
      update?.otherInformation?.bankruptcyDateFiled ??
        initialData?.otherInformation?.bankruptcyDateFiled,
    ),
    bankruptcyDescription: getValueOrNull(
      update?.otherInformation?.bankruptcyDescription ??
        initialData?.otherInformation?.bankruptcyDescription,
    ),
    bankruptcyDischarged: getValueOrNull(
      update?.otherInformation?.bankruptcyDischarged ??
        booleanToYesNo(initialData?.otherInformation?.bankruptcyDischarged),
    ),
    eviction: getValueOrNull(
      update?.otherInformation?.eviction ??
        booleanToYesNo(initialData?.otherInformation?.eviction),
    ),
    offenderRegistration: getValueOrNull(
      update?.otherInformation?.offenderRegistration ??
        booleanToYesNo(initialData?.otherInformation?.offenderRegistration),
    ),
    petsInformation:
      (
        update?.otherInformation?.petsInformation ||
        initialData?.otherInformation?.petsInformation
      )?.map((pet) => ({
        ...pet,
        breed: undefined,
        isServiceAnimal:
          typeof pet.isServiceAnimal === 'boolean'
            ? pet.isServiceAnimal
            : getBooleanFromYesNo(pet.isServiceAnimal) ?? false,
        petType: undefined,
        deletedAt: undefined,
        // createdAt: undefined,
      })) ?? [],
  };
};

interface MinorInformation {
  id?: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  preferredName?: string | null;
  age?: string | null;
  dateOfBirth: string;
  gender?: string | null;
  handicapped?: string | null;
  studentType: string;
  ethnicity?: string | null;
  race?: string | null;
  multiRace?: string[] | null;
  socialSecurityNumber?: string | null;
  alienRegistrationNumber?: string | null;
  jointCustody?: string | null;
  raceSource?: string | null;
  createdAt?: string;
  updatedAt?: string;
  applicantId?: string;
  suffixId?: string | null;
  affordableRelationship?: string;
  relationship?: string;
  legallyResponsible?: string[];
}

interface AdditionalOccupants {
  anticipateChangeHouseholdPregnancy?: number;
  anticipateChangeHousehold: string | null;
  adults?: {
    anyAdult: string,
    howManyAdults: number | null,
    adultsDescription: string | null,
    adultsInformation: any[],
    adultsArray: any[],
  };
  minors?: {
    anyMinor: string,
    minorsInformation: MinorInformation[],
  };
  needsUnitAccessible: string;
}

export interface PrimaryFormPayload {
  applicantId: string;
  skipApplicationStatusUpdate?: boolean;
  additionalOccupants: AdditionalOccupants;
  applicantsCurrentEmployer: Employment;
  applicantsSecondCurrentEmployer: Employment;
  applicantsPreviousEmployer: Employment;
  employmentInformation: {
    status: string,
    anticipateChangeEmployer: AnticipateChangeEmployer,
    studentType: string,
    hasSecondCurrentEmployer: boolean,
    hasPreviousEmployer: boolean,
  };
  statesLived?: string[];
}

export const getPrimaryFormPayload = ({
  initialData,
  update,
  states,
}: {
  initialData: PrimaryFormPayload,
  update: PrimaryFormPayload,
  states: Array<Object>,
}): PrimaryFormPayload => {
  const currentDisabilityState = {
    ...initialData?.applicantInfo?.basicInfo?.disability,
    ...update?.basicInformation?.disability,
  };

  const effectiveHasDisabilityInDisabilityObject =
    currentDisabilityState?.hasDisability;

  return {
    complete:
      getValueOrNull(update?.complete) ??
      getValueOrNull(initialData?.complete) ??
      false,
    ...(update?.skipApplicationStatusUpdate === true && {
      skipApplicationStatusUpdate: true,
    }),
    signature: {
      ...initialData?.signature,
      ...update?.signature,
    },
    basicInformation: {
      firstName: null,
      middleName: null,
      lastName: null,
      birthday: null,
      socialSecurityNumber: null,
      passportNumber: null,
      passportCountry: null,
      alienRegistration: null,
      gender: null,
      handicapped: assignHandicappedField(
        update?.basicInformation?.handicapped,
        initialData?.applicantInfo?.basicInfo?.handicapped,
      ),
      ethnicity: null,
      race: null,
      multiRace: null,
      raceSource: null,
      veteran: null,
      suffix: null,
      ...initialData?.applicantInfo?.basicInfo,
      ...update?.basicInformation,
      preferredName: getValueOrNull(
        update?.basicInformation?.preferredName ??
          initialData?.applicantInfo?.basicInfo?.preferredName,
      ),
      disability: {
        hasDisability: null,
        disabilityHearing: null,
        disabilityMobile: null,
        disabilityOther: null,
        disabilityVisual: null,
        ...initialData?.applicantInfo?.basicInfo?.disability,
        ...update?.basicInformation?.disability,
        ...(getBooleanFromYesNo(effectiveHasDisabilityInDisabilityObject) ===
          null && {
          disabilityHearing: null,
          disabilityMobile: null,
          disabilityOther: null,
          disabilityVisual: null,
        }),
      },
    },
    contactInformation: assignContactInformation(
      initialData?.applicantInfo?.contactInfo,
      update?.contactInformation,
    ),
    maritalInformation: {
      maritalStatus: getValueOrNull(
        update?.maritalInformation?.maritalStatus ??
          initialData?.applicantInfo?.maritalInfo?.maritalStatus,
      ),
      firstName: getValueOrNull(
        update?.maritalInformation?.firstName ??
          initialData?.applicantInfo?.maritalInfo?.spouse?.firstName,
      ),
      middleName: getValueOrNull(
        update?.maritalInformation?.middleName ??
          initialData?.applicantInfo?.maritalInfo?.spouse?.middleName,
      ),
      lastName: getValueOrNull(
        update?.maritalInformation?.lastName ??
          initialData?.applicantInfo?.maritalInfo?.spouse?.lastName,
      ),
      preferredName: getValueOrNull(
        update?.maritalInformation?.preferredName ??
          initialData?.applicantInfo?.maritalInfo?.spouse?.preferredName,
      ),
    },
    studentInformation: Boolean(
      update?.studentInformation?.studentType?.length ||
        initialData?.studentInfo?.studentType?.length,
    )
      ? {
          isStudent: null,
          TANFAssistance: null,
          jobTrainingProgram: null,
          singleParentDependantChild: null,
          marriedJointReturn: null,
          fosterProgram: null,
          otherStudentStatus: null,
          ...initialData?.studentInfo,
          ...update?.studentInformation,
          studentType: getValueOrEmptyString(
            update?.studentInformation?.studentType ??
              initialData?.studentInfo?.studentType,
          ),
        }
      : undefined,
    additionalOccupants: {
      anticipateChangeHouseholdAdoption: null,
      anticipateChangeHouseholdFoster: null,
      anticipateChangeHouseholdPregnancy: null,
      ...initialData?.additionalOccupants,
      ...update?.additionalOccupants,
      anticipateChangeHousehold: booleanToYesNo(
        update?.additionalOccupants?.anticipateChangeHousehold ??
          initialData?.additionalOccupants?.anticipateChangeHousehold,
      ),
      needsUnitAccessible: booleanToYesNo(
        update?.additionalOccupants?.needsUnitAccessible ??
          initialData?.additionalOccupants?.needsUnitAccessible,
      ),
      adults: {
        anyAdult: getValueOrNull(
          booleanToYesNo(update?.additionalOccupants?.adults?.anyAdult) ??
            booleanToYesNo(initialData?.additionalOccupants?.adults?.anyAdult),
        ),
        howManyAdults: getValueOrNull(
          update?.additionalOccupants?.adults?.howManyAdults ??
            initialData?.additionalOccupants?.adults?.howManyAdults,
        ),
        adultsDescription: getValueOrNull(
          update?.additionalOccupants?.adults?.adultsDescription ??
            initialData?.additionalOccupants?.adults?.adultsDescription,
        ),
        adultsInformation:
          update?.additionalOccupants?.adults?.adultsInformation ??
          initialData?.additionalOccupants?.adults?.adultsInformation ??
          [],
        adultsArray:
          update?.additionalOccupants?.adults?.adultsArray ??
          initialData?.additionalOccupants?.adults?.adultsArray ??
          [],
      },
      minors: {
        anyMinor: getValueOrNull(
          booleanToYesNo(update?.additionalOccupants?.minors?.anyMinor) ??
            booleanToYesNo(initialData?.additionalOccupants?.minors?.anyMinor),
        ),
        minorsInformation: getMinorsInformationUpdate(
          initialData?.additionalOccupants?.minors?.minorsInformation,
          update?.additionalOccupants?.minors?.minorsInformation,
        ),
      },
    },
    otherInformation: {
      ...assignOtherInformation({ initialData, update }),
    },
    currentResidenceInformation: (() => {
      const hasInitialCity = Boolean(
        initialData?.residentialInfo?.currentAddress?.city?.length,
      );
      const hasUpdateCity = Boolean(
        update?.currentResidenceInformation?.address?.city?.length,
      );
      const shouldAssign = hasInitialCity || hasUpdateCity;

      return shouldAssign
        ? assignAddress(
            initialData?.residentialInfo?.currentAddress,
            update?.currentResidenceInformation,
            states,
          )
        : {};
    })(),
    previousResidenceInformation: {
      ...(Boolean(
        initialData?.residentialInfo?.previousAddress?.city?.length ||
          update?.previousResidenceInformation?.address?.city?.length,
      ) &&
        assignAddress(
          initialData?.residentialInfo?.previousAddress,
          update?.previousResidenceInformation,
          states,
        )),
    },
    statesLived: update?.statesLived || [],
    applicantsCurrentEmployer: assignEmployment(
      initialData?.employmentInfo?.currentJob,
      update?.applicantsCurrentEmployer,
    ),
    applicantsSecondCurrentEmployer: assignEmployment(
      initialData?.employmentInfo?.secondJob,
      update?.applicantsSecondCurrentEmployer,
    ),
    applicantsPreviousEmployer: assignEmployment(
      initialData?.employmentInfo?.previousJob,
      update?.applicantsPreviousEmployer,
    ),
    employmentInformation: {
      status: getValueWithLengthOrUndefined(
        update?.employmentInformation?.status,
        initialData?.employmentInfo?.employmentStatusId,
      ),
      anticipateChangeEmployer: getAnticipateChangeEmployer(
        initialData?.employmentInfo?.anticipateChangeEmployer,
        update?.employmentInformation?.anticipateChangeEmployer,
      ),
      studentType: getValueWithLengthOrUndefined(
        update?.employmentInformation?.studentType,
        initialData?.employmentInfo?.studentType,
      ),
      hasSecondCurrentEmployer: Boolean(
        update?.employmentInformation?.hasSecondCurrentEmployer ??
          update?.applicantsSecondCurrentEmployer?.position?.length ??
          initialData?.employmentInfo?.secondJob?.position?.length,
      ),
      hasPreviousEmployer: Boolean(
        update?.employmentInformation?.hasPreviousEmployer ??
          update?.applicantsPreviousEmployer?.position?.length ??
          initialData?.employmentInfo?.previousJob?.position?.length,
      ),
    },
  };
};
